// ─────────────────────────────────────────────────────────────────────────────
// algolia-card-template.js — CB Shopify theme
//
// beforeInstantSearchProductTemplate hook — renders the product card HTML for
// every hit in the collection/search grid.
//
// Depends on window.CBAlgolia (algolia-shared.js must load first).
// ─────────────────────────────────────────────────────────────────────────────

document.addEventListener('algolia.hooks.initialize', function () {
  const { settings, alignSuffix, icons } = window.CBAlgolia

  // ── US-4: InstantSearch product card (collection + search pages) ───────────
  algoliaShopify.hooks.registerHook(
    'beforeInstantSearchProductTemplate',
    (_defaultTemplate, hit, html, components) => {
      try {
        // ── Badge logic (mirrors product-badge.liquid) ───────────────────────
        const isSoldOut = !hit.available
        const tags = Array.isArray(hit.tags) ? hit.tags : []
        const isClearance =
          tags.includes('Clearance') || tags.includes('C&B Clearance')
        const isOnSale = hit.compare_at_price > hit.price && hit.available
        const discountPct = isOnSale
          ? Math.round(
              ((hit.compare_at_price - hit.price) / hit.compare_at_price) * 100
            )
          : 0

        let badgeText = ''
        let badgeModifier = 'badge--sale'
        if (settings.showSalesBadge) {
          badgeText = settings.badgeSaleLabel || 'Sale'
          if (isClearance) {
            badgeText = settings.badgeClearanceLabel || 'Clearance'
            badgeModifier = 'badge--clearance'
          }
        }

        const showSoldOut = settings.showSoldOutBadge && isSoldOut
        const soldOutBadge = html`<span
          data-algolia-sold-out-badge
          class="badge badge--sold-out${showSoldOut ? '' : ' hidden'}"
          aria-hidden="true"
          >${settings.badgeSoldOutLabel || 'Sold out'}</span
        >`

        const showSaleBadge = settings.showSalesBadge && badgeText
        const saleBadge = html`
          ${settings.showSalesBadge
            ? html`<span
                data-algolia-sale-badge
                class="${`badge ${badgeModifier}${showSaleBadge ? '' : ' hidden'}`}"
                aria-hidden="true"
              >
                <span class="badge__label">${badgeText}</span>
              </span>`
            : ''}
          ${settings.showSavePercentage
            ? html`<div
                data-algolia-sale-pct
                class="badge save-percentage badge--save-percentage${isOnSale &&
                !isSoldOut
                  ? ''
                  : ' hidden'}"
              >
                <span class="save-percentage__value">-${discountPct}%</span>
              </div>`
            : ''}
        `

        // ── Price classes ────────────────────────────────────────────────────
        const priceClass = [
          'price',
          'product-grid-item__price',
          `justify-content-${alignSuffix}`,
          isSoldOut && 'price--sold-out',
          isOnSale && !isSoldOut && 'price--on-sale',
        ]
          .filter(Boolean)
          .join(' ')

        // ── Quick-shop button — mirrors quick-view.liquid: modal-opener + button + quick-add-modal shell
        const cartIcon = icons.cart(html)
        const closeIcon = icons.close(html)

        const productUrl = `/products/${hit.handle}`
        const modalId = `QuickAdd-${hit.objectID}`

        const quickAddButton = html`
          <div class="quick-add no-js-hidden">
            <modal-opener data-modal="#${modalId}">
              <button
                type="submit"
                name="add"
                class="quick-add__submit cart--icon-button button--icon h6 mb-0"
                aria-haspopup="dialog"
                aria-label="${settings.chooseOptionsLabel
                  ? settings.chooseOptionsLabel.replace(
                      '{{ product_name }}',
                      hit.title
                    )
                  : `Choose options for ${hit.title}`}"
                data-product-url="${productUrl}"
              >
                ${cartIcon}
              </button>
            </modal-opener>
            <quick-add-modal id="${modalId}" class="quick-add-modal">
              <div
                role="dialog"
                aria-label="${settings.chooseOptionsLabel
                  ? settings.chooseOptionsLabel.replace(
                      '{{ product_name }}',
                      hit.title
                    )
                  : `Choose options for ${hit.title}`}"
                aria-modal="true"
                class="quick-add-modal__content global-settings-popup"
                tabindex="-1"
              >
                <button
                  id="ModalClose-${hit.objectID}"
                  type="button"
                  class="quick-add-modal__toggle"
                  aria-label="${settings.accessibilityClose || 'Close'}"
                >
                  ${closeIcon}
                </button>
                <div
                  id="QuickAddInfo-${hit.objectID}"
                  class="quick-add-modal__content-info"
                ></div>
              </div>
            </quick-add-modal>
          </div>
        `

        const productLink = algoliaShopify.helpers.instantsearchLink(hit)

        return html`
          <div
            class="product-grid-item"
            data-algolia-handle="${hit.handle}"
            data-algolia-objectid="${hit.objectID}"
            onClick="${() => algoliaShopify.helpers.handleItemClick(hit)}"
          >
            <div class="product-grid-item__thumbnail">
              ${settings.cardBadgesEnabled
                ? html`<span
                    class="product-grid-item__badges badge_desktop--top-left"
                  >
                    ${soldOutBadge} ${saleBadge}
                  </span>`
                : ''}

              <a
                href="${productLink}"
                data-algolia-card-link
                class="d-block product__media_thumbnail product__card--link"
              >
                <div
                  class="media media--transparent media--adapt media--hover-effect"
                >
                  ${(() => {
                    const img = algoliaShopify.helpers.renderImage(hit, 533)
                    return html`<img
                      data-algolia-primary-img
                      src="${img.src}"
                      srcset="${img.srcset}"
                      sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
                      alt="${hit.title}"
                      loading="lazy"
                      class="motion-reduce"
                    />`
                  })()}
                </div>
              </a>

              <ul class="product-grid-item__actions style2 justify-content-end">
                <li>${quickAddButton}</li>
              </ul>
            </div>

            <div
              class="product-grid-item__content text-${settings.productContentAlignment ||
              'center'}"
            >
              <div data-swatch-zone></div>

              <h3 class="product-grid-item__title h6">
                <a href="${productLink}" data-algolia-card-link>
                  ${components.Highlight({ attribute: 'title', hit })}
                </a>
              </h3>

              <div class="${priceClass}" data-algolia-price>
                <div class="price__container">
                  <div
                    class="price__sale${isOnSale && !isSoldOut
                      ? ''
                      : ' hidden'}"
                  >
                    <span class="visually-hidden visually-hidden--inline"
                      >${settings.priceRegularLabel || 'Regular price'}</span
                    >
                    <span
                      ><s
                        class="price-item price-item--regular"
                        data-algolia-price-compare
                        >${algoliaShopify.helpers.displayStrikedPrice(
                          hit.price,
                          hit.compare_at_price
                        )}</s
                      ></span
                    >
                    <span class="visually-hidden visually-hidden--inline"
                      >${settings.priceSaleLabel || 'Sale price'}</span
                    >
                    <span
                      class="price-item price-item--sale price-item--last"
                      data-algolia-price-sale
                      >${window.CBAlgolia.fromPrice(
                        Math.round(hit.price * 100)
                      )}</span
                    >
                  </div>
                  <div
                    class="price__regular${isOnSale && !isSoldOut
                      ? ' hidden'
                      : ''}"
                  >
                    <span class="visually-hidden visually-hidden--inline"
                      >${settings.priceRegularLabel || 'Regular price'}</span
                    >
                    <span
                      class="price-item price-item--regular"
                      data-algolia-price-regular
                      >${window.CBAlgolia.fromPrice(
                        Math.round(hit.price * 100)
                      )}</span
                    >
                  </div>
                </div>
              </div>

              ${settings.cardBadgesEnabled
                ? html`<div class="mobile-badge">
                    <span
                      class="product-grid-item__badges badge_desktop--top-left"
                    >
                      ${soldOutBadge} ${saleBadge}
                    </span>
                  </div>`
                : ''}
            </div>
          </div>
        `
      } catch (err) {
        console.error(
          '[algolia] beforeInstantSearchProductTemplate error:',
          err.stack || err,
          hit
        )
        return _defaultTemplate
      }
    }
  )
})
